import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import {
    deleteShopMember,
    getShopInviteCode,
    getShopMembers,
    regenerateShopInviteCode,
} from "../../helpers/backend_helper";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import type { AppDispatch } from "../../store";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

const ShopMembers = () => {
    const dispatch = useDispatch<AppDispatch>();
    const currentUserId = useSelector((state: any) => state.auth?.user?.id);

    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
    const [inviteData, setInviteData] = useState<any | null>(null);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getShopMembers();
            const responseData = response?.data ?? response;
            const resolvedMembers = responseData?.data ?? responseData;
            setMembers(Array.isArray(resolvedMembers) ? resolvedMembers : []);
        } catch (e: any) {
            const message = resolveApiMessage(e);
            setError(message);
            dispatch(showAlertWithTimeout("danger", "Thất bại", message));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const fetchInviteCode = useCallback(async () => {
        setInviteLoading(true);
        setInviteError(null);

        try {
            const response = await getShopInviteCode();
            const responseData = response?.data ?? response;
            setInviteData(responseData?.data ?? responseData ?? null);
        } catch (e: any) {
            const message = resolveApiMessage(e);
            setInviteError(message);
        } finally {
            setInviteLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInviteCode();
    }, [fetchInviteCode]);

    const memberList = useMemo(() => (Array.isArray(members) ? members : []), [members]);

    const handleDeleteMember = async () => {
        if (!deleteTarget?.id) return;

        try {
            const response = await deleteShopMember(deleteTarget.id);
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
            setDeleteTarget(null);
            fetchMembers();
        } catch (e) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(e)));
        }
    };

    const handleRegenerateInviteCode = async () => {
        try {
            const response = await regenerateShopInviteCode();
            const responseData = response?.data ?? response;
            setInviteData(responseData?.data ?? responseData ?? null);
            dispatch(showAlertWithTimeout("success", "Thành công", "Đã tạo lại mã mời."));
        } catch (e) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(e)));
        }
    };

    const handleCopyInvite = async () => {
        const inviteCode = inviteData?.invite_code;
        if (!inviteCode || !navigator?.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText(inviteCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (e) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", "Không thể sao chép mã mời."));
        }
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={!!deleteTarget}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content={`Bạn có chắc chắn muốn xóa ${deleteTarget?.name ?? "thành viên"} khỏi quán?`}
                onConfirm={handleDeleteMember}
                onCancel={() => setDeleteTarget(null)}
            />

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />
                    <div className="page-heading"><h6 className="mb-0">Thành viên</h6></div>
                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        <div className="card mb-3">
                            <div className="card-body">
                                <h6 className="mb-2">Mời thành viên bằng mã</h6>
                                {inviteLoading && <p className="text-muted mb-0">Đang tải mã mời...</p>}
                                {!inviteLoading && inviteError && (
                                    <p className="text-danger mb-0">{inviteError}</p>
                                )}
                                {!inviteLoading && !inviteError && inviteData && (
                                    <>
                                        <small className="text-muted d-block mb-2">
                                            Mã mời: <strong>{inviteData?.invite_code}</strong>
                                        </small>
                                        <small className="text-muted d-block mb-3">
                                            Hết hạn: {inviteData?.invite_expires_at ? new Date(inviteData.invite_expires_at).toLocaleString() : "--"}
                                        </small>
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-sm btn-primary" onClick={handleCopyInvite}>
                                                {copied ? "Đã sao chép" : "Sao chép mã"}
                                            </button>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={handleRegenerateInviteCode}>
                                                Tạo lại mã
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {loading && <p className="text-center text-muted">Đang tải danh sách thành viên...</p>}

                        {!loading && memberList.length === 0 && (
                            <p className="text-center text-muted">{error ?? "Chưa có thành viên nào."}</p>
                        )}

                        {memberList.map((member: any) => {
                            const isCurrentUser = String(member?.id) === String(currentUserId);
                            const isOwner = member?.is_owner === true;

                            return (
                                <div className="cart-table card mb-3" key={member.id}>
                                    <div className="table-responsive card-body d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-1">{member?.name ?? "Không có tên"}</h6>
                                            <small className="text-muted d-block">{member?.email ?? "Không có email"}</small>
                                            <div className="mt-2 d-flex gap-2">
                                                {isCurrentUser && <span className="badge bg-info">Bạn</span>}
                                                {isOwner && <span className="badge bg-primary">Chủ quán</span>}
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-sm btn-danger text-white"
                                            disabled={isCurrentUser || isOwner}
                                            onClick={() => setDeleteTarget(member)}
                                        >
                                            Xóa
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ShopMembers;
